/**
 * Cookie consent banner.
 *
 * Stratégie :
 *   1. Si Shopify a déjà rendu sa bannière native (`#shopify-pc__banner`)
 *      → on ne fait rien, on laisse Shopify gérer.
 *   2. Si `Shopify.customerPrivacy.shouldShowBanner()` retourne true mais
 *      qu'aucune bannière n'est dans le DOM (cas Webflow où le snippet
 *      Shopify n'a pas tourné) :
 *      a) on tente d'injecter le script `customer-privacy.js` Shopify
 *         (qui rendrait sa bannière standard).
 *      b) si après 1.5s rien n'apparait → fallback : on affiche notre
 *         mini-bannière LMDR brand-styled avec 3 actions :
 *           - "Refuser tout" → setConsent({analytics:false, marketing:false})
 *           - "Accepter tout" → setConsent({analytics:true, marketing:true})
 *           - "Personnaliser" → ouvre un modal détaillé (3 catégories)
 *
 * L'intégrateur peut aussi mettre des boutons custom dans Webflow :
 *   <button data-consent="accept-all">Accepter</button>
 *   <button data-consent="reject-all">Refuser</button>
 *   <button data-consent="customize">Personnaliser</button>
 *   <button data-consent="open-banner">Gérer mes cookies</button>
 *
 * On bind ces clics même si la bannière n'est pas affichée (utile pour
 * un footer "Gérer mes cookies").
 */

import {
  setConsent,
  shouldShowBanner,
  consentGiven,
  isRegulatedRegion,
  analyticsAllowed,
  marketingAllowed,
  preferencesAllowed,
} from "./consent.js";

const FALLBACK_ID = "lmdr-cookie-banner";
const MODAL_ID = "lmdr-cookie-modal";
const SHOPIFY_BANNER_ID = "shopify-pc__banner";

function cssSafe(s: string): string {
  return s.replace(/[<>&"']/g, (c) => ({
    "<": "&lt;", ">": "&gt;", "&": "&amp;", '"': "&quot;", "'": "&#39;",
  }[c] ?? c));
}

function buildFallbackBanner(): HTMLElement {
  const el = document.createElement("div");
  el.id = FALLBACK_ID;
  el.setAttribute("role", "dialog");
  el.setAttribute("aria-label", "Gestion des cookies");
  el.style.cssText = [
    "position:fixed",
    "left:16px",
    "right:16px",
    "bottom:16px",
    "max-width:560px",
    "margin:0 auto",
    "background:#fff",
    "color:#1a1a1a",
    "border:1px solid rgba(0,0,0,0.1)",
    "border-radius:8px",
    "box-shadow:0 8px 32px rgba(0,0,0,0.12)",
    "padding:20px 24px",
    "font-family:'Cochinltstd',Georgia,serif",
    "font-size:14px",
    "line-height:1.5",
    "z-index:99999",
  ].join(";");

  el.innerHTML = `
    <div style="margin-bottom:12px;font-size:15px;color:#1a1a1a;">
      Nous utilisons des cookies pour assurer le bon fonctionnement du site
      (panier, langue) et, avec votre accord, pour mesurer l'audience et
      améliorer votre expérience.
    </div>
    <div style="display:flex;flex-wrap:wrap;gap:8px;align-items:center;">
      <button type="button" data-lmdr-banner-action="reject"
        style="padding:8px 16px;background:transparent;color:#760000;border:1px solid #760000;border-radius:4px;cursor:pointer;font-family:inherit;">
        Refuser
      </button>
      <button type="button" data-lmdr-banner-action="customize"
        style="padding:8px 16px;background:transparent;color:#444;border:1px solid #ccc;border-radius:4px;cursor:pointer;font-family:inherit;">
        Personnaliser
      </button>
      <button type="button" data-lmdr-banner-action="accept"
        style="padding:8px 16px;background:#760000;color:#fff;border:1px solid #760000;border-radius:4px;cursor:pointer;font-family:inherit;flex:1;min-width:120px;">
        Accepter
      </button>
      <a href="/politique-confidentialite" target="_blank" rel="noopener"
        style="margin-left:auto;color:#760000;font-size:13px;text-decoration:underline;">
        En savoir plus
      </a>
    </div>
  `;
  return el;
}

function buildCustomizeModal(): HTMLElement {
  const el = document.createElement("div");
  el.id = MODAL_ID;
  el.setAttribute("role", "dialog");
  el.setAttribute("aria-label", "Personnaliser mes cookies");
  el.style.cssText = [
    "position:fixed",
    "inset:0",
    "background:rgba(0,0,0,0.5)",
    "z-index:100000",
    "display:flex",
    "align-items:center",
    "justify-content:center",
    "padding:16px",
  ].join(";");

  el.innerHTML = `
    <div style="background:#fff;border-radius:8px;padding:32px;max-width:520px;width:100%;font-family:'Cochinltstd',Georgia,serif;font-size:14px;line-height:1.5;color:#1a1a1a;max-height:90vh;overflow-y:auto;">
      <h2 style="margin:0 0 8px;font-size:20px;">Personnaliser mes cookies</h2>
      <p style="margin:0 0 20px;font-size:13px;color:#666;">
        Vous pouvez accepter ou refuser chaque catégorie indépendamment.
        Les cookies essentiels restent actifs car ils sont nécessaires
        au fonctionnement du site (panier, langue, etc.) — exemptés
        de consentement par la CNIL.
      </p>

      <div style="margin-bottom:14px;padding:12px;background:#f7f5f3;border-radius:6px;">
        <label style="display:flex;align-items:center;gap:10px;cursor:not-allowed;opacity:0.7;">
          <input type="checkbox" checked disabled style="cursor:not-allowed;">
          <span><strong>Strictement nécessaires</strong> <em style="color:#666;font-size:12px;font-style:normal;">— toujours actifs</em></span>
        </label>
        <div style="font-size:12px;color:#666;margin-top:6px;margin-left:26px;">
          Identifiant de panier, langue/devise, choix de cookies.<br>
          Sans ces cookies, le site ne peut pas fonctionner.
        </div>
      </div>

      <div style="margin-bottom:14px;padding:12px;background:#fafafa;border-radius:6px;border:1px solid #eee;">
        <label style="display:flex;align-items:center;gap:10px;cursor:pointer;">
          <input type="checkbox" id="lmdr-cookie-pref" data-lmdr-cat="preferences">
          <span><strong>Préférences</strong></span>
        </label>
        <div style="font-size:12px;color:#666;margin-top:6px;margin-left:26px;">
          Produits récemment vus et mémorisation passive de votre navigation.
          La liste de souhaits, activée par vos ajouts, reste disponible même en cas de refus.
          Elle est conservée dans ce navigateur pendant 365 jours après votre dernière modification,
          sans prolongation lors des simples visites. Retirez les articles pour les supprimer.
        </div>
      </div>

      <div style="margin-bottom:14px;padding:12px;background:#fafafa;border-radius:6px;border:1px solid #eee;">
        <label style="display:flex;align-items:center;gap:10px;cursor:pointer;">
          <input type="checkbox" id="lmdr-cookie-ana" data-lmdr-cat="analytics">
          <span><strong>Mesure d'audience</strong></span>
        </label>
        <div style="font-size:12px;color:#666;margin-top:6px;margin-left:26px;">
          Statistiques anonymisées sur l'utilisation du site
          via Shopify Analytics. Aucun tracker tiers (Google, Meta).
        </div>
      </div>

      <div style="margin-bottom:24px;padding:12px;background:#fafafa;border-radius:6px;border:1px solid #eee;">
        <label style="display:flex;align-items:center;gap:10px;cursor:pointer;">
          <input type="checkbox" id="lmdr-cookie-mkt" data-lmdr-cat="marketing">
          <span><strong>Marketing</strong></span>
        </label>
        <div style="font-size:12px;color:#666;margin-top:6px;margin-left:26px;">
          Personnalisation publicitaire et retargeting.
          Permet aux campagnes Shopify de mesurer leur efficacité.
        </div>
      </div>

      <div style="display:flex;gap:8px;">
        <button type="button" data-lmdr-banner-action="close"
          style="flex:1;padding:10px;background:transparent;color:#444;border:1px solid #ccc;border-radius:4px;cursor:pointer;font-family:inherit;">
          Annuler
        </button>
        <button type="button" data-lmdr-banner-action="save-prefs"
          style="flex:2;padding:10px;background:#760000;color:#fff;border:1px solid #760000;border-radius:4px;cursor:pointer;font-family:inherit;font-weight:bold;">
          Enregistrer mes préférences
        </button>
      </div>
    </div>
  `;

  // Pré-coche selon état actuel (chaque catégorie indépendamment)
  setTimeout(() => {
    const pref = el.querySelector<HTMLInputElement>("#lmdr-cookie-pref");
    const ana = el.querySelector<HTMLInputElement>("#lmdr-cookie-ana");
    const mkt = el.querySelector<HTMLInputElement>("#lmdr-cookie-mkt");
    if (pref) pref.checked = preferencesAllowed();
    if (ana) ana.checked = analyticsAllowed();
    if (mkt) mkt.checked = marketingAllowed();
  }, 0);

  return el;
}

function dismissBanner(): void {
  document.getElementById(FALLBACK_ID)?.remove();
  document.getElementById(MODAL_ID)?.remove();
}

function openCustomizeModal(): void {
  if (document.getElementById(MODAL_ID)) return;
  document.body.appendChild(buildCustomizeModal());
}

function handleAction(action: string): void {
  switch (action) {
    case "accept":
      setConsent({ analytics: true, marketing: true, preferences: true });
      dismissBanner();
      break;
    case "reject":
      setConsent({ analytics: false, marketing: false, preferences: false });
      dismissBanner();
      break;
    case "customize":
      openCustomizeModal();
      break;
    case "save-prefs": {
      const modal = document.getElementById(MODAL_ID);
      const pref = modal?.querySelector<HTMLInputElement>("#lmdr-cookie-pref")?.checked ?? false;
      const ana = modal?.querySelector<HTMLInputElement>("#lmdr-cookie-ana")?.checked ?? false;
      const mkt = modal?.querySelector<HTMLInputElement>("#lmdr-cookie-mkt")?.checked ?? false;
      setConsent({
        preferences: pref,
        analytics: ana,
        marketing: mkt,
      });
      dismissBanner();
      break;
    }
    case "close":
      document.getElementById(MODAL_ID)?.remove();
      break;
    case "open-banner":
      // Re-affiche la bannière (pour bouton "Gérer mes cookies" footer)
      if (!document.getElementById(FALLBACK_ID)) {
        document.body.appendChild(buildFallbackBanner());
      }
      break;
  }
}

function attachGlobalClickHandler(): void {
  if ((document as unknown as { __lmdrBannerBound?: boolean }).__lmdrBannerBound) return;
  (document as unknown as { __lmdrBannerBound?: boolean }).__lmdrBannerBound = true;

  // Bind notre fallback (data-lmdr-banner-action)
  document.addEventListener("click", (ev) => {
    const el = (ev.target as HTMLElement | null)?.closest<HTMLElement>(
      "[data-lmdr-banner-action]",
    );
    if (!el) return;
    ev.preventDefault();
    const action = el.getAttribute("data-lmdr-banner-action") ?? "";
    handleAction(action);
  });

  // Bind boutons consent custom dans Webflow (data-consent="accept-all|reject-all|customize|open-banner")
  document.addEventListener("click", (ev) => {
    const el = (ev.target as HTMLElement | null)?.closest<HTMLElement>(
      "[data-consent]",
    );
    if (!el) return;
    ev.preventDefault();
    const action = el.getAttribute("data-consent") ?? "";
    const map: Record<string, string> = {
      "accept-all": "accept",
      "reject-all": "reject",
      "customize": "customize",
      "open-banner": "open-banner",
      "manage": "open-banner",
    };
    handleAction(map[action] ?? action);
  });
}

function shopifyBannerInDom(): boolean {
  return !!document.getElementById(SHOPIFY_BANNER_ID);
}

/**
 * Au boot : vérifie si la bannière doit s'afficher. Si Shopify a déjà
 * rendu la sienne, on ne fait rien. Sinon on affiche notre fallback.
 *
 * Idempotent.
 */
export function attachCookieBanner(): void {
  if (typeof document === "undefined") return;
  attachGlobalClickHandler();

  // Si pas en zone régulée → pas de bannière
  if (!isRegulatedRegion()) return;

  // Si l'user a déjà répondu (yes/no) → pas de bannière
  if (consentGiven()) return;

  // Si Shopify n'estime pas qu'il faut l'afficher → on fait confiance
  if (!shouldShowBanner()) return;

  // Attend 1.5s : Shopify peut rendre sa bannière en async
  setTimeout(() => {
    if (shopifyBannerInDom()) return; // Shopify a fait son boulot
    if (consentGiven()) return; // l'user a cliqué entre-temps
    // Fallback : on affiche notre bannière
    if (!document.getElementById(FALLBACK_ID)) {
      document.body.appendChild(buildFallbackBanner());
    }
  }, 1500);
}
